/**
 * @file Page-lifecycle helper seeding the ContractCreate page's state from the
 * selected unit: default charge templates are copied into drafts, each
 * inheriting the template's own `paymentRouteId`.
 *
 * Flow: ContractCreate `middlewares` → reads the unit and its
 * `defaultChargeTemplates` → `setState` of the create draft.
 * Exceptions: none.
 */
import { Middleware } from "../Middleware";
import { MiddlewareInterface } from "../MiddlewareInterface";
import Constants from "../../Action/Constants";
import Time from "../../Time/Time";
import Formatter from "../../Localization/Formatter";
import { UnitInterface } from "../../Entity/Entities/Unit/UnitInterface";
import { PersonInterface } from "../../Entity/Entities/Person/PersonInterface";
// import AgencyFeeContainer from "../../Entity/Entities/AgencyFee/AgencyFeeContainer";
// import { AgencyFeeInterface } from "../../Entity/Entities/AgencyFee/AgencyFeeInterface";
// import AgencyFeeFactory from "../../Entity/Factories/AgencyFeeFactory";

class ContractCreateWillMount extends Middleware implements MiddlewareInterface {
  public component: any;

  constructor() {
    super();
  }

  public process(): void {
    const unitId: number = parseInt(this.component.props.params.unitId, 10);
    const unit: UnitInterface = this.component.props.units.byId[unitId];

    const personId: number = parseInt(this.component.props.params.personId, 10);
    const person: PersonInterface = this.component.props.app.persons.find(person => person.id === personId);

    this.component.props.dispatch({
      type: Constants.GLOBAL_CONTRACT_CREATE_CLEAR
    });

    const usingBeta: boolean = this.component.props.app?.settings?.user?.beta;

    const state: any = {
      isAgencyFeeDialogOpen: false,
      agencyFee: null,
      anchorElAgencyFees: null,
      beta: usingBeta,
      defaultAgencyFees: [],
      selectedUnit: unit,
      selectedTenant: person
    };

    if (Object.keys(this.component.props.app?.savedDataBeforeRedirecting || {}).length > 0) {
      const savedData = {...this.component.props.app.savedDataBeforeRedirecting.dataToBeSaved};
      const savedPeople = !!this.component.props.app.savedDataBeforeRedirecting.dataToBeSaved.ignoredPeople &&
                          this.component.props.app.savedDataBeforeRedirecting.dataToBeSaved.ignoredPeople ?
                            [...this.component.props.app.savedDataBeforeRedirecting.dataToBeSaved.ignoredPeople] :
                            [];
      const createdPerson = this.component.props.app.savedDataBeforeRedirecting.createdPerson ?
                              this.component.props.app.savedDataBeforeRedirecting.createdPerson :
                              null;
      const ignoredPeople = createdPerson ? [{...createdPerson}, ...savedPeople] : [...savedPeople];
      const createdUnit = this.component.props.app.savedDataBeforeRedirecting.createdUnit ?
                            this.component.props.app.savedDataBeforeRedirecting.createdUnit :
                            null;

      savedData.ignoredPeople = [...ignoredPeople];

      if (createdPerson) {
        savedData.selectedTenant = createdPerson;
      }

      if (createdUnit) {
        savedData.selectedUnit = createdUnit;
      }

      savedData.lastSelectedIndex = null;

      this.component.setState({
        ...state,
        ...savedData
      }, () => {
        this.component.props.dispatch({
          type: Constants.UNIT_SAVED_DATA_BEFORE_REDIRECTING_CLEAR
        });
      });
    } else {
      if (unit) {
        // if a previous contract exist with the end date set,
        // then set the default value for the first date of the new contract to the next day
        // else take first date of next month from today
        const unitContracts = unit.contracts.sort((contract1, contract2) => {
          if (contract1.firstDate < contract2.firstDate) {
            return 1;
          }

          if (contract1.firstDate > contract2.firstDate) {
            return -1;
          }

          return  0;
        });

        const firstDate = !!unitContracts[0] && !!unitContracts[0].endDate ? Time.nextDayFromDate(unitContracts[0].endDate) : Time.firstDateNextMonth();
        const lastDate = Time.nextYearPrevMonthLastDay(firstDate);
        const expirationDate = Time.substractMonth(Time.nextYearPrevMonthLastDay(firstDate), this.component.props.app.settings.agency.notificationPeriod);

        this.component.setState({
          firstDate: firstDate,
          lastDate: lastDate,
          expirationDate: expirationDate
        });

        // when user want to create contract we should copy charge templates from unit
        let chargeTemplates = [];
        unit.defaultChargeTemplates.forEach(template => {
          let chargeTemplate = {
            amount: parseFloat(Formatter.simpleFormatNumber(template.amount).replace(",", ".")),
            currency: template.currency.getCurrencyId(),
            currencyId: template.currency.getCurrencyId(),
            type: template.type.id,
            chargeClassId: template.type.chargeClassId,
            repeatPeriod: template.repeatPeriod.id,
            repeatPeriodId: template.repeatPeriod.id,
            description: template.description,
            paymentOffset: template.paymentOffset?.getValue(),
            dueDateFirstPayment: firstDate,
            firstDate: firstDate,
            endDate: Time.formatOrNull(template.endDate),
            isDefault: false,
            paymentRouteId: template.paymentRouteId,
            isCollective: template?.isCollective
          }

          chargeTemplates.push(chargeTemplate);
        });

        this.component.setState({
          chargeTemplates: chargeTemplates
        })

        // when user want to create contract we should copy agency fee from parent agency
        // const agencyFeeToCopy: AgencyFeeInterface | null = unit.defaultAgencyFees[0] || unit.inheritedAgencyFee;

        // if (agencyFeeToCopy) {
        //   const agencyFee = {
        //     objectType: AgencyFeeContainer.pickIdFromLabel("Contract"),
        //     objectId: null,
        //     currency: agencyFeeToCopy.currency.getCurrencyId(),
        //     fixedFee: agencyFeeToCopy.fixedFee,
        //     percentageFee: agencyFeeToCopy.percentageFee,
        //     fixedPlusPercentageMode: agencyFeeToCopy.fixedPlusPercentageMode.id
        //   }

        //   const copiedAgencyFee: AgencyFeeInterface = AgencyFeeFactory.create(agencyFee);

        //   this.component.setState({
        //     defaultAgencyFees: [copiedAgencyFee]
        //   });
        // }
      }
    }
  }
}

export default new ContractCreateWillMount();
